import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ExchangeRateService, ExchangeRateConfigDTO, ExchangeRateConfigRequestDTO } from '../../../services/exchange-rate.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';

@Component({
  selector: 'app-exchange-rate-config',
  templateUrl: './exchange-rate-config.component.html',
  styleUrls: ['./exchange-rate-config.component.scss']
})
export class ExchangeRateConfigComponent implements OnInit {
  configForm: FormGroup;
  currentConfig: ExchangeRateConfigDTO | null = null;
  isLoading = false;
  submitting = false;
  serviceStatus = false;

  constructor(
    private fb: FormBuilder,
    private exchangeRateService: ExchangeRateService,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) {
    this.configForm = this.fb.group({
      isEnabled: [false],
      baseUrl: ['', [Validators.required, Validators.maxLength(500)]],
      apiKey: ['', [Validators.required, Validators.maxLength(200)]],
      updateIntervalHours: [4, [Validators.required, Validators.min(1), Validators.max(168)]]
    });
  }

  ngOnInit(): void {
    this.loadConfiguration();
    this.loadServiceStatus();
  }

  /**
   * Carga la configuración actual del servicio
   */
  loadConfiguration(): void {
    this.isLoading = true;
    this.loadingService.show();
    
    this.exchangeRateService.getConfiguration().subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          this.currentConfig = response.data;
          this.populateForm(response.data);
        } else {
          this.toastService.showToast('error', 'Error al cargar la configuración');
        }
        this.isLoading = false;
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading configuration:', error);
        this.toastService.showToast('error', 'Error al cargar la configuración del sistema');
        this.isLoading = false;
        this.loadingService.hide();
      }
    });
  }

  /**
   * Carga el estado actual del servicio
   */
  private loadServiceStatus(): void {
    this.exchangeRateService.getServiceStatus().subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.serviceStatus = response.isEnabled;
        }
      },
      error: (error) => {
        console.error('Error loading service status:', error);
      }
    });
  }

  /**
   * Llena el formulario con los datos de configuración
   */
  private populateForm(config: ExchangeRateConfigDTO): void {
    this.configForm.patchValue({
      isEnabled: config.isEnabled,
      baseUrl: config.baseUrl,
      apiKey: '', // No mostramos la API key completa por seguridad
      updateIntervalHours: config.updateIntervalHours
    });
  }

  /**
   * Guarda la configuración
   */
  onSubmit(): void {
    if (this.configForm.invalid || this.submitting) {
      this.configForm.markAllAsTouched();
      return;
    }

    // Confirmación para cambios críticos
    if (!this.configForm.get('isEnabled')?.value && this.currentConfig?.isEnabled) {
      const confirmed = confirm(
        '⚠️ Está a punto de deshabilitar el servicio de Exchange Rate.\n\n' +
        'Esto afectará todas las conversiones de moneda en el sistema.\n\n' +
        '¿Está seguro de que desea continuar?'
      );
      if (!confirmed) {
        return;
      }
    }

    this.submitting = true;
    this.loadingService.show();

    const configRequest: ExchangeRateConfigRequestDTO = {
      isEnabled: this.configForm.get('isEnabled')?.value || false,
      baseUrl: this.configForm.get('baseUrl')?.value || '',
      apiKey: this.configForm.get('apiKey')?.value || '',
      updateIntervalHours: this.configForm.get('updateIntervalHours')?.value || 4
    };

    this.exchangeRateService.updateConfiguration(configRequest).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.toastService.showToast('success', 'Configuración actualizada exitosamente');
          this.loadConfiguration(); // Recargar para obtener datos actualizados
          this.loadServiceStatus(); // Actualizar estado del servicio
        } else {
          this.toastService.showToast('error', response.message || 'Error al actualizar la configuración');
        }
      },
      error: (error) => {
        console.error('Error updating configuration:', error);
        this.toastService.showToast('error', 'Error al actualizar la configuración');
      },
      complete: () => {
        this.submitting = false;
        this.loadingService.hide();
      }
    });
  }

  /**
   * Actualiza manualmente las tasas de cambio
   */
  updateRates(): void {
    if (!this.serviceStatus) {
      this.toastService.showToast('warning', 'El servicio de Exchange Rate está deshabilitado');
      return;
    }

    this.loadingService.show();
    this.exchangeRateService.updateRates().subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.toastService.showToast('success', response.message || 'Tasas actualizadas exitosamente');
          this.loadConfiguration(); // Recargar para ver última fecha de actualización
        } else {
          this.toastService.showToast('error', response.message || 'Error al actualizar las tasas');
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error updating rates:', error);
        this.toastService.showToast('error', 'Error al actualizar las tasas de cambio');
        this.loadingService.hide();
      }
    });
  }

  /**
   * Comprueba si un campo del formulario tiene un error específico
   */
  hasError(controlName: string, errorType: string): boolean {
    const control = this.configForm.get(controlName);
    return control?.touched && control?.hasError(errorType) || false;
  }

  /**
   * Verifica si el formulario es válido y si hay cambios
   */
  get isFormValid(): boolean {
    return this.configForm.valid;
  }

  /**
   * Formatea fechas para mostrar
   */
  formatDate(dateString?: string): string {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Obtiene el color del estado del servicio
   */
  getStatusColor(): string {
    return this.serviceStatus ? 'text-green-600' : 'text-red-600';
  }

  /**
   * Obtiene el texto del estado del servicio
   */
  getStatusText(): string {
    return this.serviceStatus ? 'Habilitado' : 'Deshabilitado';
  }
}
